import { useEffect, useState } from 'react';
import { Eye, EyeOff, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ['all', 'pending', 'approved', 'hidden'];
const STATUS_CLS = {
  pending: 'bg-yellow-500/10 text-yellow-400',
  approved: 'bg-green-500/10 text-green-400',
  hidden: 'bg-[rgba(255,255,255,0.06)] text-[#A3A19C]',
};

export const AdminReviews = ({ token }) => {
  const { t, fmtDateTime } = useLang();
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('all');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/reviews`, { headers }).then(r => setReviews(r.data)).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/reviews/${id}`, { status }, { headers });
      setReviews(reviews.map(r => (r.id === id ? { ...r, status } : r)));
      toast.success(t('rev.updated'));
    } catch { toast.error(t('admin.update_fail')); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('rev.confirm_delete'))) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers });
      setReviews(reviews.filter(r => r.id !== id));
      toast.success(t('rev.deleted'));
    } catch { toast.error(t('admin.update_fail')); }
  };

  const shown = filter === 'all' ? reviews : reviews.filter(r => r.status === filter);
  const btn = "text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors duration-300";

  return (
    <div data-testid="admin-reviews">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('rev.admin_title')}</h2>
          <p className="text-sm text-[#A3A19C]">{t('rev.admin_sub')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`reviews-filter-${f}`}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors duration-300 ${filter === f ? 'bg-[#D4AF37] text-[#08080A]' : 'border border-[rgba(255,255,255,0.1)] text-[#A3A19C] hover:text-[#F5F2EB]'}`}>
              {f === 'all' ? t('rev.filter_all') : t(`rev.status_${f}`)} ({f === 'all' ? reviews.length : reviews.filter(r => r.status === f).length})
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        {shown.map(r => (
          <div key={r.id} className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-6" data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-[#F5F2EB]">{r.user_name}</p>
                <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-semibold ${STATUS_CLS[r.status]}`}>{t(`rev.status_${r.status}`)}</span>
                <span className="text-[10px] text-[#8C6C73]">{fmtDateTime(r.created_at)}</span>
              </div>
              <div className="flex gap-2">
                {r.status !== 'approved' && (
                  <button onClick={() => setStatus(r.id, 'approved')} className={`${btn} border-green-500/30 text-green-400 hover:bg-green-500/10`} data-testid={`approve-review-${r.id}`}>
                    <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('rev.approve')}
                  </button>
                )}
                {r.status !== 'hidden' && (
                  <button onClick={() => setStatus(r.id, 'hidden')} className={`${btn} border-[rgba(255,255,255,0.1)] text-[#A3A19C] hover:text-[#F5F2EB]`} data-testid={`hide-review-${r.id}`}>
                    <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('rev.hide')}
                  </button>
                )}
                <button onClick={() => remove(r.id)} className={`${btn} border-red-500/30 text-red-400 hover:bg-red-500/10`} data-testid={`delete-review-${r.id}`}>
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {t('rev.delete')}
                </button>
              </div>
            </div>
            <p className="text-sm text-[#A3A19C] leading-relaxed" dir="auto">{r.text}</p>
            {r.user_email && <p className="text-[10px] text-[#8C6C73] mt-2 ltr-iso">{r.user_email}</p>}
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-[#A3A19C] text-center py-12">{t('rev.empty')}</p>}
      </div>
    </div>
  );
};
